<template>
  <b-container>
    <b-form-group :disabled="changes_disabled">
      <b-card no-body class="mt-3">
        <b-card-header
          >{{ $t('connection') }}
          <b-link
            :href="
              `https://cx-oracle.readthedocs.io/en/${oracle_cx_version}/user_guide/installation.html#overview`
            "
            target="_blank"
            ><span :id="uid + 'connection_help'" class="fas fa-question-circle"
          /></b-link>
          <b-tooltip :target="uid + 'connection_help'">{{
            $t('connection_help')
          }}</b-tooltip>
        </b-card-header>
        <b-card-body>
          <b-form-checkbox
            v-model="is_new_database"
            class="mb-4"
            switch
            size="lg"
          >
            {{ $t('new_database') }}
          </b-form-checkbox>
          <template v-if="is_new_database">
            <b-form-group>
              <label>
                <b>
                  {{ $t('username') }}
                </b>
              </label>
              <b-form-input v-model="username" type="text" key="username" />
            </b-form-group>
            <b-form-group>
              <label>
                <b>
                  {{ $t('password') }}
                </b>
              </label>
              <b-form-input v-model="password" type="password" key="password" />
            </b-form-group>
            <b-form-group>
              <label>
                <b>
                  {{ $t('dsn') }}
                </b>
              </label>
              <b-form-input v-model="dsn" type="text" key="dsn" />
            </b-form-group>
          </template>
          <!--           <b-form-group v-else>
            <label>
              <b>
                {{ $t('database') }}
              </b>
            </label>
            <b-form-select
              v-model="database_name"
              :options="databases_options"
            />
          </b-form-group> -->
          <b-form-group v-else>
            <label>
              <b>
                {{ $t('database') }}
              </b>
            </label>
            <b-form-input
              v-model="database_name"
              type="text"
              list="databases-list"
              autocomplete="off"
              key="databases-list"
            />
            <b-form-datalist id="databases-list" :options="databases_options" />
          </b-form-group>
          <b-form-group>
            <label>
              <b>
                {{ $t('encoding') }}
              </b>
            </label>
            <b-form-input v-model="encoding" type="text" />
          </b-form-group>
          <b-form-group>
            <label>
              <b>
                {{ $t('nencoding') }}
              </b>
            </label>
            <b-form-input v-model="nencoding" type="text" />
          </b-form-group>
        </b-card-body>
      </b-card>

      <b-card :header="$t('sql_query')" class="mt-3">
        <b-form-group>
          <label>
            <b>
              {{ $t('sql_query') }}
            </b>
            <b-link
              :href="
                `https://cx-oracle.readthedocs.io/en/${oracle_cx_version}/user_guide/bind.html`
              "
              target="_blank"
              ><span :id="uid + 'sql_query_help'" class="fas fa-question-circle"
            /></b-link>
            <b-tooltip :target="uid + 'sql_query_help'">{{
              $t('sql_query_help')
            }}</b-tooltip>
          </label>
          <!-- <code-editor v-model="query_string" :disabled="changes_disabled" /> -->
          <togglable-code-editor
            v-model="query_string"
            :disabled="changes_disabled"
          />
        </b-form-group>
        <b-form-group>
          <label>
            <b>
              {{ $t('batch_size') }}
            </b>
            <b-link
              :href="
                `https://cx-oracle.readthedocs.io/en/${oracle_cx_version}/user_guide/sql_execution.html#tuning-fetch-performance`
              "
              target="_blank"
              ><span
                :id="uid + 'batch_size_help'"
                class="fas fa-question-circle"
            /></b-link>
            <b-tooltip :target="uid + 'batch_size_help'">{{
              $t('batch_size_help')
            }}</b-tooltip>
          </label>
          <b-form-input v-model="batch_size" type="number" />
        </b-form-group>
      </b-card>

      <b-card :header="$t('query_parameters')" class="mt-3">
        <b-form-group>
          <b-form-checkbox
            v-model="custom_query_parameters"
            :value="true"
            :unchecked-value="false"
          >
            {{ $t('custom_query_parameters') }}
            <b-link
              :href="
                `https://cx-oracle.readthedocs.io/en/${oracle_cx_version}/user_guide/bind.html`
              "
              target="_blank"
              ><span
                :id="uid + 'custom_query_parameters_help'"
                class="fas fa-question-circle"
            /></b-link>
            <b-tooltip :target="uid + 'custom_query_parameters_help'">{{
              $t('custom_query_parameters_help')
            }}</b-tooltip>
          </b-form-checkbox>

          <togglable-code-editor
            v-model="query_parameters_code"
            :disabled="changes_disabled"
          />
          <b-form-checkbox
            v-model="use_project_code"
            :value="true"
            :unchecked-value="false"
            >{{ $t('use_project_code') }}
          </b-form-checkbox>
        </b-form-group>
      </b-card>

      <!--       <unroll-element :title="$t('code_stdout')" :available="true">
        <pre>{{ code_stdout }}</pre>
      </unroll-element> -->

      <unroll-element :title="$t('code_stdout')" :available="true">
        <unroll-element
          v-for="(poid, index) in metavariables_dicts_poids"
          :key="poid"
          :title="stringified_dict(poid)"
          :available="true"
        >
          <pre>{{ metavariables_dicts_code_stdout[index] }}</pre>
        </unroll-element>
      </unroll-element>
    </b-form-group>
  </b-container>
</template>

<i18n>
{
  "en": {
    "connection": "Connection",
    "connection_help": "You need to install the Oracle client libraries on the COsMO hosting Server if not the same server than the database. Click for reference documentation",
    "new_database": "New database connection",
    "database": "Database",
    "username": "Username",
    "password": "Password",
    "dsn": "DSN (Data Source Name)",
    "encoding": "Encoding (Generic Client Character Set)",
    "nencoding": "N-Encoding (National Client Character Set)",
    "sql_query": "SQL Query",
    "batch_size": "Batch Size",
    "query_parameters": "Query Parameters",
    "custom_query_parameters": "Use Custom Query Parameters",
    "query_parameters_code": "Query Parameters Code",
    "code_stdout": "Standard output",
    "sql_query_help": "You can use bind variables, the values of which are either the metavariables as a default or custom defined below (custom query parameters). Click for reference documentation",
    "batch_size_help": "Click for reference documentation",
    "custom_query_parameters_help": "You can execute Python code returning a dictionary the key-value pairs of which will be use to define bind variables. Click for reference documentation",
    "use_project_code": "Use project code"
  },
  "fr": {
    "connection": "Connexion",
    "connection_help": "Vous devez installer les bibliothèques Oracle client sur le serveur hébergeant COsMO si la base de données est sur un serveur différent. Cliquez pour consulter la documentation de référence",
    "new_database": "Connexion à une nouvelle base de données",
    "database": "Base de données",
    "username": "Nom d'utilisateur",
    "password": "Mot de passe",
    "dsn": "DSN (Data Source Name)",
    "encoding": "Encoding (Jeu de Caractères Client Générique)",
    "nencoding": "N-Encoding (Jeu de Caractères Client National)",
    "sql_query": "Requête SQL",
    "batch_size": "Taille de Batch",
    "query_parameters": "Paramètres de Requête",
    "custom_query_parameters": "Utiliser des Paramètres de Requête Personnalisés",
    "query_parameters_code": "Code des Paramètres de Requête",
    "code_stdout": "Sortie standard",
    "sql_query_help": "Vous pouvez utiliser des 'bind variables', dont les valeurs sont soit les métavariables par défaut ou personnalisées et définies plus bas (paramètres de requête personnalisés). Cliquez pour consulter la documentation de référence",
    "batch_size_help": "Cliquez pour consulter la documentation de référence",
    "custom_query_parameters_help": "Vous pouvez exécuter du code Python retournant un dictionnaire dont les couples clé-valeur serviront à définir les 'bind variables'. Cliquez pour consulter la documentation de référence",
    "use_project_code": "Utiliser le code spécifique au projet"
  }
}
</i18n>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'

import { BListGroupItem } from 'bootstrap-vue'
import TogglableCodeEditor from '@/components/togglable_code_editor.vue'

import { table_columns } from '@/api/subscriptions.js'

import {
  global_brick_mixin,
  client_encryption_mixin,
  brick_with_single_parent_mixin,
  brick_tracking_info_mixin,
} from '@/components/mixin.js'

import UnrollElement from '@/components/unroll_element.vue'

import { BasicTableInfoEntity } from '@/components/object_model.js'

// *****************************************************************************

// *****************************************************************************

export default {
  name: 'BrickDatabase',
  components: {
    TogglableCodeEditor,
    UnrollElement,
  },
  mixins: [global_brick_mixin, client_encryption_mixin],
  props: ['brick_id', 'project_id', 'description_name', 'metavariables_dict'],
  data() {
    return {
      is_new_database: false,

      decrypted_username: '',
      decrypted_password: '',
      decrypted_dsn: '',
      username: '',
      password: '',
      dsn: '',
      database_name: '',
      encoding: 'UTF-8',
      nencoding: 'UTF-8',
      query_string: '',
      batch_size: 10000,
      query_parameters_code: '',
      custom_query_parameters: false,
      oracle_cx_version: '7.3',
      use_project_code: false,
    }
  },
  computed: {
    // code_stdout() {
    //   let res = (this.tracking_info.code_output || {})['bound_parameters'] || ''
    //   return res
    // },

    metavariables_dicts_code_stdout() {
      let res = _.map(
        this.tracking_infos,
        elt => ((elt || {}).code_output || {})['bound_parameters'] || ''
      )
      return res
    },

    databases_credentials() {
      let res = _.map(
        this.automl_databases_credentials_undeleted_object.lists || [],
        v =>
          this.automl_databases_credentials_undeleted_object
            .databases_credentials[v]
      )
      return res
    },

    // databases_options() {
    //   let res = [{ value: '', text: this.$t('choose_a_database') }]
    //   _.forEach(this.databases_credentials, elt => {
    //     res.push({ text: elt.database_name, value: elt.database_name })
    //   })
    //   return res
    // },
    databases_options() {
      // let res = [{ value: '', text: this.$t('choose_a_database') }]
      // _.forEach(this.databases_credentials, elt => {
      //   res.push({ text: elt.database_name, value: elt.database_name })
      // })
      // let res = [{ value: '', text: this.$t('choose_a_database') }]
      const res = _.map(this.databases_credentials, elt => elt.database_name)

      return res
    },

    // adapted_data() {
    //   let username, password, dsn
    //   // console.log('username')
    //   if (this.username === this.decrypted_username) {
    //     username = this.cur_brick.username
    //   } else {
    //     username = this.client_encrypt_string(this.username)
    //   }
    //   // console.log('password')
    //   if (this.password === this.decrypted_password) {
    //     password = this.cur_brick.password
    //   } else {
    //     password = this.client_encrypt_string(this.password)
    //   }
    //   // console.log('dsn')
    //   if (this.dsn === this.decrypted_dsn) {
    //     dsn = this.cur_brick.dsn
    //   } else {
    //     dsn = this.client_encrypt_string(this.dsn)
    //   }
    //   // console.log('done')
    //   let adapted = {
    //     uid: this.brick_id,
    //     username: username,
    //     password: password,
    //     dsn: dsn,
    //     encoding: this.encoding,
    //     nencoding: this.nencoding,
    //     query_string: this.query_string,
    //     batch_size: this.batch_size,
    //     query_parameters_code: this.custom_query_parameters
    //       ? this.query_parameters_code
    //       : '',
    //     use_project_code: this.use_project_code,
    //   }

    //   _.assignIn(adapted, this.description_name)

    //   return adapted
    // },

    adapted_data() {
      let username, password, dsn
      // console.log('username')
      if (this.username === this.decrypted_username) {
        username = this.cur_brick.username
      } else {
        username = this.client_encrypt_string(this.username)
      }
      // console.log('password')
      if (this.password === this.decrypted_password) {
        password = this.cur_brick.password
      } else {
        password = this.client_encrypt_string(this.password)
      }
      // console.log('dsn')
      if (this.dsn === this.decrypted_dsn) {
        dsn = this.cur_brick.dsn
      } else {
        dsn = this.client_encrypt_string(this.dsn)
      }

      // console.log('done')
      let adapted = {
        uid: this.brick_id,
        is_new_database: this.is_new_database,
        database_name: this.database_name,
        username: username,
        password: password,
        dsn: dsn,
        encoding: this.encoding,
        nencoding: this.nencoding,
        query_string: this.query_string,
        batch_size: this.batch_size,
        query_parameters_code: this.custom_query_parameters
          ? this.query_parameters_code
          : '',
        use_project_code: this.use_project_code,
      }

      _.assignIn(adapted, this.description_name)

      return adapted
    },

    /*    adapted_data() {
      let adapted = {
        uid: this.brick_id,
        database_name: this.database_name,
        encoding: this.encoding,
        nencoding: this.nencoding,
        query_string: this.query_string,
        batch_size: this.batch_size,
        query_parameters_code: this.custom_query_parameters
          ? this.query_parameters_code
          : '',
        use_project_code: this.use_project_code,
      }

      _.assignIn(adapted, this.description_name)

      return adapted
    },*/
    ...mapGetters(['automl_databases_credentials_undeleted_object']),
  },
  watch: {
    cur_brick: {
      immediate: true,
      handler(nv) {
        // console.log('nv:', nv)

        this.decrypted_username = this.default_client_decrypt_string(
          nv.username,
          { default_value: '' }
        )
        this.decrypted_password = this.default_client_decrypt_string(
          nv.password,
          { default_value: '' }
        )
        this.decrypted_dsn = this.default_client_decrypt_string(nv.dsn, {
          default_value: '',
        })
        this.username = this.decrypted_username
        this.password = this.decrypted_password
        this.dsn = this.decrypted_dsn

        this.is_new_database = nv.is_new_database
        this.database_name = nv.database_name

        this.encoding = nv.encoding
        this.nencoding = nv.nencoding
        this.query_string = nv.query_string
        this.batch_size = nv.batch_size
        this.custom_query_parameters = nv.query_parameters_code !== ''
        this.query_parameters_code = nv.query_parameters_code
        this.use_project_code = nv.use_project_code
      },
    },
  },

  beforeMount() {
    this.fetch_collections(['databases_credentials'])
  },
}
</script>
